import {useDefault} from '../../../common/common';
import {DateUtils} from '../../../common/date.utils';
// builders
import {ProductUseBuilder} from './product-use.builder';
import {OfferBuilder} from './offer.builder';
import {UserProfileBuilder} from './user-profile.builder';
// Reservation V2 interfaces
import {IHotelReservationV2} from '../../interfaces/HotelReservationV2/IHotelReservation-IV2';
import {IHotel} from '../../interfaces/HotelReservationV2/IHotel-IV2';
import {IReservationIds} from '../../interfaces/HotelReservationV2/IReservationIds-IV2';
import {IExternalConfirmationNumber} from '../../interfaces/HotelReservationV2/IReservationIds-IV2';
import {IConfirmationNumbers} from '../../interfaces/HotelReservationV2/IReservationIds-IV2';
import {ISegment} from '../../interfaces/HotelReservationV2/ISegment';
import {IUserProfile} from '../../interfaces/HotelReservationV2/IUserProfile-IV2';
import {IPersonName} from '../../interfaces/HotelReservationV2/IPersonName';
import {IProductUseGuestCount} from '../../interfaces/HotelReservationV2/IProductUseGuestCount';
import {ITimePeriod} from '../../interfaces/HotelReservationV2/ITimePeriod';
import {IOffer} from '../../interfaces/HotelReservationV2/IOffer';
import {IProductUse} from '../../interfaces/HotelReservationV2/IProductUse';
import {IPayment} from '../../interfaces/HotelReservationV2/IPayment';
import {ICommunicationPreferences} from '../../interfaces/HotelReservationV2/ICommunicationPreferences';

// Models
import {Reservation} from '../../model/reservation/reservation.model';
import {Stay} from '../../model/reservation/stay.model';
import {Room} from '../../model/reservation/room/room.model';
import {Rate} from '../../model/reservation/rate/rate.model';
import {Guest} from '../../model/guest/guest.model';
import {Hotel} from '../../model/hotel/hotel.model';
import {ReservationStatusType} from '../../model/reservation/reservation-status.model';
import {ReservationChangeType} from '../../model/reservation/change/reservation-change.model';
import {SegmentReferenceIds} from '../../model/reservation/segmentReferenceIds.model';
import {CommunicationPreferences} from '../../model/communication/communication.preferences.model';

export class ReservationV2RequestBuilder {
  private _request: IHotelReservationV2;
  private _productUseBuilder: ProductUseBuilder;
  private _offerBuilder: OfferBuilder;
  private _userProfileBuilder: UserProfileBuilder;

    constructor() {
        this._request = <IHotelReservationV2>{};
        this._request.hotels = []; //<IHotel>
        this._request.reservationIds = <IReservationIds>{};
        this._request.segments = []; //<ISegment>
        this._request.userProfiles = []; //<IUserProfile>

        this._productUseBuilder = new ProductUseBuilder();
        this._offerBuilder = new OfferBuilder();
        this._userProfileBuilder = new UserProfileBuilder();
    }

    withReservation(reservation: Reservation) {
        const stay = useDefault(reservation.stay, new Stay());
        const room = useDefault(reservation.room, new Room());
        const rate = useDefault(reservation.rate, new Rate());
        const guest = useDefault(reservation.guest, new Guest());
        const definedAdditionalGuests = useDefault(reservation.additionalGuests, []);
        // collect data
        const cid = DateUtils.toShortISODate(stay.checkInDate);
        const cod = DateUtils.toShortISODate(stay.checkOutDate);
        const ratePlanCode = rate.code;
        const groupCode = rate.groupCode || undefined;
        const roomCode = room.code;

        // add product and offer info
        this._productUseBuilder
            .withTimePeriod(<ITimePeriod>{start: cid, end: cod})
            .withQuantity(stay.numberOfRooms)
            .withAdults(stay.numberOfAdults)
            .withChildren(stay.numberOfChildren)
            .withProductCode(roomCode);
      if (groupCode) {
        this._offerBuilder
          .withGroupCode(groupCode)
          .withProductUse(this._productUseBuilder.build());
      } else {
        this._offerBuilder
          .withRatePlanCode(ratePlanCode)
          .withProductUse(this._productUseBuilder.build());
      }
        this._request.reservationIds = <IReservationIds>{
            confirmationNumbers: [
              /* V2 Note: ihgConfirmationNumber should be used for all basic reservation flows.
              Update 11/29/18: ExternalConfirmationNumber will only be used for Venetian & Pallazzo Reservations. However, v2 service for these scenarios has not completed yet.
            */
              // <IConfirmationNumbers>{externalConfirmationNumber: <IExternalConfirmationNumber>{number: reservation.confirmationNumber}
              // }
              <IConfirmationNumbers>{ihgConfirmationNumber: reservation.confirmationNumber}
            ]
        };
        this._request.hotels.push(<IHotel>{hotelMnemonic: reservation.hotelMnemonic});
        this.withGuest(guest);
        this.withDefinedAdditionalGuests(definedAdditionalGuests);
        this.withHotelAndOffer(reservation.hotelMnemonic, this._offerBuilder.build());

        return this;
    }

    withHotelAndOffer(hotelMnemonic: string, offer: any) {
        this._request.segments.splice(0, 1, <ISegment>{hotelMnemonic: hotelMnemonic, offer});
        return this;
    }

    withIds(reservation: Reservation) {
      const ids = useDefault(reservation.segmentReferenceIds, new SegmentReferenceIds());
      this._request.segments[0].id = ids.segmentId;
      this._request.segments[0].guestId = ids.guestId;
      return this;
    }

    withPayment(reservation: Reservation, availData?: any) {
      const paymentObj = useDefault(reservation.payment, {});
      const creditCard = useDefault(paymentObj.creditCard, null);
      let guaranteeType = useDefault(paymentObj.guaranteeType, 'DAY_OF_ARRIVAL_HOLD');
      let paymentInformation;
      let deposit;

      const offerCode = useDefault(reservation.offerCode, null);
      if (offerCode) { // only for free night. dont continue with this function if there is an offerCode;
        this._request.segments[0].payment = <IPayment>{offerCode: offerCode};
        return this;
      }

      let card = null;
      if (creditCard && creditCard.token && creditCard.type && creditCard.expirationDate) {
        card = {
          code: creditCard.type,
          number: creditCard.token,
          expireDate: creditCard.expirationDate,
        };
      }

      // add the things that come from the new availability data
      if (availData) {
        const rateDetails = availData.hotels && availData.hotels.length && availData.hotels[0].rateDetails ? availData.hotels[0].rateDetails : {};
        const policies = rateDetails.offers && rateDetails.offers.length && rateDetails.offers[0]  && rateDetails.offers[0].policies ? rateDetails.offers[0].policies : null;
        if (policies && policies.deposit && policies.deposit.isRequired) {
          if (guaranteeType && policies.guarantee && policies.guarantee.type) { // isDepositRequired because only deposit policy rates need guaranteeType overridden
            guaranteeType = policies.guarantee.type;
            if (!(guaranteeType === 'NEED_DEPOSIT' || guaranteeType === 'PAID_DEPOSIT')) {
              // Reset deposit amount if guarantee type does not equal NEED_DEPOSIT or PAID_DEPOSIT
              deposit = 0;
            } else {
              deposit = useDefault(policies.deposit.amount, '0');
            }
          }
        }
      }

      // only add the things we have to the object. extra fields without values can break the request
      if (deposit && card) {
        paymentInformation = {depositAmount: deposit, card: card};
      } else if (deposit) {
        paymentInformation = {depositAmount: deposit};
      } else if (card) {
        paymentInformation = {card: card};
      }

      this._request.segments[0].payment = <IPayment>{guaranteeType: guaranteeType, paymentInformation: paymentInformation};
    
      return this;
    }

    withCommunicationPreferences(reservation: Reservation) {
      const communicationPreferences = useDefault(reservation.communicationPreferences, new CommunicationPreferences());
      this._request.segments[0].communicationPreferences = <ICommunicationPreferences>{emailAddresses: [communicationPreferences.emailAddress], sendEmail: communicationPreferences.sendEmail, smsNumbers: [communicationPreferences.smsNumber], sendSMS: communicationPreferences.sendSMS, faxNumbers: [communicationPreferences.faxNumber]};
      return this;
    }

    withGuest(guest: Guest) {
        this._request.userProfiles.splice(0, 1, <IUserProfile>this._userProfileBuilder.withGuest(guest).build());
        this._productUseBuilder.withGuestId(guest.id);

        return this;
    }

    withAdults (adults: number) {
        this._offerBuilder.withProductUse(
            this._productUseBuilder.withAdults(adults).build()
        );
        this.withHotelAndOffer(this._request.hotels[0].hotelMnemonic, this._offerBuilder.build());
        return this;
    }

    withNumberOfRooms(numberOfRooms: number) {
        this._offerBuilder.withProductUse(
            this._productUseBuilder.withQuantity(numberOfRooms).build()
        );
        this.withHotelAndOffer(this._request.hotels[0].hotelMnemonic, this._offerBuilder.build());
        return this;
    }

    withChildren(children: number) {
        this._offerBuilder.withProductUse(
            this._productUseBuilder.withChildren(children).build()
        );
        this.withHotelAndOffer(this._request.hotels[0].hotelMnemonic, this._offerBuilder.build());
        return this;
    }

    withRate (rateCode: string) {
        this._offerBuilder.withRatePlanCode(rateCode)
        this.withHotelAndOffer(this._request.hotels[0].hotelMnemonic, this._offerBuilder.build());
        return this;
    }

    withRoom (roomCode: string) {
        this._offerBuilder.withProductUse(
            this._productUseBuilder.withProductCode(roomCode).build()
        );
        this.withHotelAndOffer(this._request.hotels[0].hotelMnemonic, this._offerBuilder.build());
        return this;
    }

    withCheckInDate (date: Date) {
        this._offerBuilder.withProductUse(
            this._productUseBuilder.withCheckInDate(<string>DateUtils.toShortISODate(date)).build()
        );
        this.withHotelAndOffer(this._request.hotels[0].hotelMnemonic, this._offerBuilder.build());
        return this;
    }

    withCheckOutDate (date: Date) {
        this._offerBuilder.withProductUse(
            this._productUseBuilder.withCheckOutDate(<string>DateUtils.toShortISODate(date)).build()
        );
        this.withHotelAndOffer(this._request.hotels[0].hotelMnemonic, this._offerBuilder.build());
        return this;
    }

    withStatus (statusType: ReservationStatusType) {
        this._request.imageStatus = statusType.toString();
        return this;
    }

    withChanges (changes: Map<ReservationChangeType, any>) {
        changes.forEach((value: any, key: ReservationChangeType) => {
          switch (key) {
            case ReservationChangeType.CHECK_IN_DATE:
              this.withCheckInDate(value);
              break;
            case ReservationChangeType.CHECK_OUT_DATE:
              this.withCheckOutDate(value);
              break;
            case ReservationChangeType.NUMBER_OF_ADULTS:
              this.withAdults(value);
              break;
            case ReservationChangeType.NUMBER_OF_CHILDREN:
              this.withChildren(value);
              break;
            case ReservationChangeType.NUMBER_OF_ROOMS:
              this.withNumberOfRooms(value);
              break;
            case ReservationChangeType.RATE:
              this.withRate(value);
              break;
            case ReservationChangeType.ROOM:
              this.withRoom(value);
              break;
            case ReservationChangeType.ADDITIONAL_GUEST_NAMES:
              this.withAdditionalGuests(value);
              break;
          }
        });
        return this;
    }

    withReservationRateChanges(rate: Rate, mnemonic: string): ReservationV2RequestBuilder {
        /*
        if (Hotel.isLMS(mnemonic)) {
          // We will allow to do this only for LMS  reservations that require the client to ser the price
        }
        */
        return this;
    }

    withDefinedAdditionalGuests(additionalGuests: Array<Guest>) {
        for (const additionalGuest of additionalGuests) {
          this._request.userProfiles.push(<IUserProfile>{id: additionalGuest.id, personName: <IPersonName>{surname: additionalGuest.lastName, given: additionalGuest.firstName}});
          this._productUseBuilder.withGuestId(additionalGuest.id)
      }

        return this;
    }

    withAdditionalGuests(additionalGuests: Array<Guest>) {
        let id = -1;
        for (const additionalGuest of additionalGuests) {
          this._request.userProfiles.push(<IUserProfile>{id: id, personName: <IPersonName>{surname: additionalGuest.lastName, given: additionalGuest.firstName}});
          this._productUseBuilder.withGuestId(id)
          id--
        }

        return this;
    }

    build (): IHotelReservationV2 {
        return this._request;
    }

}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/builders/reservation-v2-request.builder.ts